import { Link } from 'react-router-dom'
import MetaTags from '../components/MetaTags'
import DinBilpartnerBadge from '../components/DinBilpartnerBadge'
import ReviewsSlider from '../components/ReviewsSlider'
import ContactForm from '../components/ContactForm'
import { imageUrl } from '../lib/images'

export default function AutovaerkstedGrindstedPage({ settings }) {
  const bookingUrl = settings?.booking_url || 'https://service.dinbilpartner.dk/book-tid'
  const phone = settings?.phone || '22 18 73 30'
  const phoneRaw = settings?.phone_raw || '22187330'

  return (
    <>
      <MetaTags
        title="Autoværksted nær Grindsted | EK Biler i Hejnsvig"
        description="Leder du efter et autoværksted tæt på Grindsted? EK Biler i Hejnsvig tilbyder service, reparation, dækskift og gratis lånebil til fair pris."
        ogTitle="Autoværksted nær Grindsted | EK Biler i Hejnsvig"
        ogDescription="Leder du efter et autoværksted tæt på Grindsted? EK Biler i Hejnsvig tilbyder service, reparation, dækskift og gratis lånebil til fair pris."
        ogImage={imageUrl('img-7698-min-scaled.jpg', 'width=1200,quality=85,format=auto')}
      />

      {/* Hero Header */}
      <section className="bg-[#14171a] text-white py-14 sm:py-20 border-b border-[#262c33] relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src={imageUrl('img-7698-min-scaled.jpg', 'width=1600,quality=80,format=auto')}
            alt="Autoværksted nær Grindsted"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl space-y-4">
            <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <Link to="/" className="hover:text-white">Forside</Link>
              <span>/</span>
              <span className="text-[#e20019]">Områder</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Autoværksted nær Grindsted
            </h1>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
              Alsidigt værksted i Hejnsvig – kun ca. 10 minutters kørsel fra Grindsted centrum. Vi
              arbejder med alle bilmærker og modeller samt elbiler og hybrider.
            </p>
            <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white px-6 py-3 rounded-xl font-bold text-sm shadow-md transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-red-500 text-center"
              >
                <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                <span>Book tid til værkstedet</span>
              </a>
              <a
                href={`tel:${phoneRaw}`}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#20252c] hover:bg-[#2b323c] border border-gray-700 text-white px-5 py-3 rounded-xl font-bold text-sm transition-colors whitespace-nowrap text-center"
              >
                <i className="fa-solid fa-phone text-[#e20019]" aria-hidden="true" />
                <span>Ring på {phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content from Scrape */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column */}
            <div className="lg:col-span-7 space-y-8 text-gray-800 leading-relaxed text-base">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  Autoværksted nær Grindsted til service og reparation
                </h2>
                <p>
                  Leder du efter et autoværksted nær Grindsted, hvor du kan få hjælp til både
                  almindelig bilservice, fejlfinding og reparation? Hos EK Biler kan du få din bil
                  serviceret på et alsidigt værksted i Hejnsvig, som ligger få minutters kørsel fra
                  Grindsted. Vi arbejder med alle bilmærker og modeller – uanset om du kører i en nyere
                  bil, en ældre model, en elbil eller en hybrid.
                </p>
                <p className="mt-3">
                  Når bilen skal på værksted, er det vigtigt, at du får en tydelig vurdering af opgaven
                  og ved, hvad der skal laves. Vi undersøger bilen grundigt og rådgiver dig om den
                  relevante løsning, inden arbejdet går i gang. På den måde får du et bedre overblik
                  over både bilens tilstand og den nødvendige reparation.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Autoværksted med moderne udstyr og seks autolifte
                </h2>
                <p>
                  På vores autoværksted råder vi over seks autolifte og moderne udstyr, som gør det
                  muligt at udføre mange forskellige opgaver på samme adresse. Vi har blandt andet
                  erfaring med biler fra VAG-gruppen, herunder Volkswagen, Audi, SEAT og Škoda, men du
                  kan få hjælp, uanset hvilket bilmærke du kører i.
                </p>
                <ul className="list-disc list-inside space-y-2 mt-4 pl-2 text-gray-700">
                  <li>
                    <Link to="/serviceeftersyn" className="text-[#e20019] font-bold hover:underline">
                      Serviceeftersyn
                    </Link>{' '}
                    efter fabrikantens forskrifter
                  </li>
                  <li>Reparation af forskellige bilmærker og modeller</li>
                  <li>Klargøring af bilen til syn</li>
                  <li>Diagnoseudlæsning og fejlsøgning</li>
                  <li>Bremsekontrol og udskiftning af bremser</li>
                  <li>Airconditionservice og opfyldning</li>
                  <li>Reparation af forsikringsskader og rudeskift</li>
                  <li>Firehjulsudmåling</li>
                  <li>
                    <Link to="/daekskift" className="text-[#e20019] font-bold hover:underline">
                      Dækskift
                    </Link>{' '}
                    og{' '}
                    <Link to="/daekhotel" className="text-[#e20019] font-bold hover:underline">
                      dækopbevaring
                    </Link>
                  </li>
                  <li>
                    <Link to="/elbil-service" className="text-[#e20019] font-bold hover:underline">
                      Elbil service
                    </Link>{' '}
                    (ring for pris)
                  </li>
                </ul>
                <p className="mt-4 text-sm text-gray-700">
                  Vi arbejder efter producentens forskrifter, så du kan bevare fabriksgarantien på en
                  nyere bil, selvom vi er et uautoriseret værksted. Det betyder, at du kan vælge et
                  lokalt værksted uden nødvendigvis at skulle gå på kompromis med bilens servicehistorik
                  eller gældende garanti.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">En del af Din Bil Partner</h2>
                <p>
                  EK Biler har været en del af Din Bil Partner siden januar 2023. Vi arbejder ud fra
                  kædens principper for kompetent rådgivning og godt håndværk. For dig betyder det, at du
                  får hjælp fra et lokalt værksted, som samtidig er en del af et større
                  værkstedskoncept.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                  Gratis lånebil, mens din bil er på værksted
                </h2>
                <p>
                  Din hverdag behøver ikke gå i stå, fordi bilen skal repareres eller til service. Du kan
                  få en gratis lånebil, mens din egen bil befinder sig på værkstedet. Det gør det
                  nemmere at passe arbejde, hente børn eller klare andre daglige gøremål.
                </p>
                <p className="mt-2 font-medium text-gray-900">
                  Vores timepris er 595 kr. ekskl. moms. Inden arbejdet går i gang, får du et estimat på
                  opgaven.
                </p>
              </div>

              {/* FAQs from Scrape */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  FAQ – Ofte stillede spørgsmål om autoværksted
                </h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Hvor tæt ligger i på Grindsted?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Vi ligger på Mejerivej 12 i Hejnsvig, kun ca. 10 minutters kørsel fra Grindsted
                      centrum.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Hvad kan jeres autoværksted nær Grindsted hjælpe med?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Du kan få hjælp til blandt andet serviceeftersyn, reparation, fejlfinding,
                      bremsekontrol, airconditionservice, dækskift og klargøring til syn. Vi arbejder med
                      alle bilmærker og modeller samt elbiler og hybrider.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Kan I servicere min nye bil, uden at fabriksgarantien bortfalder?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Ja, vi udfører service efter fabrikantens forskrifter, så fabriksgarantien kan
                      bevares. Det gælder også, selvom vi er et uautoriseret værksted.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Hvad koster det at få bilen repareret?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Det samlede beløb afhænger blandt andet af fejlen, reparationens omfang,
                      tidsforbruget og de nødvendige reservedele. Timeprisen er 595 kr. ekskl. moms, og du
                      får et estimat, inden vi begynder på arbejdet.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Kan jeg låne en bil under reparationen?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Ja, du kan få en gratis lånebil, mens din egen bil er på værksted. Det gør det
                      lettere for dig at få hverdagen til at hænge sammen, indtil bilen er køreklar igen.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">Arbejder I også med elbiler?</h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Ja, du kan få udført service på elbiler hos os. Den konkrete opgave vurderes ud fra
                      bilen og dens servicebehov.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Sticky Booking Card & Contact Form */}
            <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-24">
              <div className="bg-[#14171a] text-white p-6 sm:p-8 rounded-2xl border border-gray-800 space-y-4">
                <div className="text-xs font-bold text-[#e20019] uppercase tracking-wider">
                  Nær Grindsted
                </div>
                <h3 className="text-2xl font-bold">Bestil værkstedstid</h3>
                <p className="text-sm text-gray-300">
                  Kun 10 minutters kørsel fra Grindsted. Book tid online eller ring til os.
                </p>
                <div className="pt-2">
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full text-center inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white py-3.5 px-6 rounded-xl font-bold text-sm shadow-md transition-all whitespace-nowrap"
                  >
                    <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                    <span>Book tid online nu</span>
                  </a>
                </div>
              </div>

              <ContactForm
                title="Send en forespørgsel"
                subtitle="Vi vender tilbage for at fuldføre din booking"
                bookingUrl={bookingUrl}
              />
            </div>
          </div>
        </div>
      </section>

      <DinBilpartnerBadge />
      <ReviewsSlider />
    </>
  )
}
